'use client';

import { create } from 'zustand';
import type { AxiosError } from 'axios';
import api from '@/lib/api';
import type { User } from '@xue-ying-yu/shared';

export type VerificationChannel = 'EMAIL' | 'PHONE';
export type VerificationPurpose = 'REGISTER' | 'RESET_PASSWORD';

export interface SendCodeParams {
  target: string;
  channel: VerificationChannel;
  purpose: VerificationPurpose;
}

export interface RegisterParams {
  inviteCode: string;
  target: string;
  channel: VerificationChannel;
  verificationCode: string;
  password: string;
  nickname: string;
}

export interface LoginParams {
  identifier: string;
  password?: string;
  verificationCode?: string;
}

export interface ResetPasswordParams {
  target: string;
  code: string;
  newPassword: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (params: LoginParams) => Promise<void>;
  register: (params: RegisterParams) => Promise<void>;
  sendCode: (params: SendCodeParams) => Promise<void>;
  sendLoginCode: (params: { target: string; channel: VerificationChannel }) => Promise<void>;
  forgotPassword: (params: { target: string; channel: VerificationChannel }) => Promise<void>;
  resetPassword: (params: ResetPasswordParams) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  loading: true,

  login: async ({ identifier, password, verificationCode }) => {
    const body: { identifier: string; password?: string; verificationCode?: string } = {
      identifier,
    };
    if (password) body.password = password;
    if (verificationCode) body.verificationCode = verificationCode;
    const res = await api.post('/auth/login', body);
    set({ user: res.data.user });
  },

  register: async ({ inviteCode, target, channel, verificationCode, password, nickname }) => {
    const body: Record<string, string> = {
      inviteCode,
      verificationCode,
      password,
      nickname,
    };
    if (channel === 'EMAIL') body.email = target;
    else body.phone = target;
    const res = await api.post('/auth/register', body);
    set({ user: res.data.user });
  },

  sendCode: async ({ target, channel, purpose }) => {
    await api.post('/auth/send-code', { target, channel, purpose });
  },

  sendLoginCode: async ({ target, channel }) => {
    await api.post('/auth/login/send-code', { target, channel });
  },

  forgotPassword: async ({ target, channel }) => {
    await api.post('/auth/forgot-password', { target, channel });
  },

  resetPassword: async ({ target, code, newPassword }) => {
    await api.post('/auth/reset-password', { target, code, newPassword });
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.error('Failed to logout:', e);
    }
    set({ user: null });
  },

  changePassword: async (oldPassword, newPassword) => {
    await api.put('/auth/password', { oldPassword, newPassword });
  },

  fetchUser: async () => {
    try {
      const res = await api.get('/auth/me');
      set({ user: res.data, loading: false });
    } catch (e) {
      const axiosErr = e as AxiosError;
      if (axiosErr.response?.status !== 401) {
        console.error('Failed to fetch user:', e);
      }
      set({ user: null, loading: false });
    }
  },
}));
